/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState, useCallback } from "react";
import { View } from "react-native";
import { NetworkStatus, useLazyQuery } from "@apollo/client";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";
import { DashboardTableTabType } from "./types";

import { Text } from "UpduoLibrary";
import {
  skaleMainBlue,
  skaleWebTextGray,
  upduoLightestGray,
} from "config/UpduoColors";
import Breakdown from "./Breakdown";
import Activity from "./Activity";
import SkillProgress from "./SkillProgress";
import TableHeader from "./TableTabHeader";
import CsvModal from "./CsvModal";
import { callApi } from "config/UpduoService";
import {
  formattedDateString,
  getNodes,
  getQueryString,
  useOnScrollToBottom,
} from "config/Utilities";
import { GET_STAFF_SESSION_LIST } from "graphql/internal/Queries";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import {
  GetSessionListQuery,
  SkaleSessionType,
} from "graphql/internal/generated";
import { FilterSet } from "../types";
import { getSessions, SessionsCollection } from "./Activity/useActivity";
import { PropagateLoader } from "react-spinners";

// TODO this type could be a lot nicer
interface DashboardFilter {
  regionId: string | null;
  districtId: string | null;
  storeId: string | null;
  userId: string | null;
  skillId: string | null;
  dateRangeStart: Date;
  dateRangeEnd: Date;
}

interface TableViewProps {
  isUser: boolean;
  dataDescription: string;
  dashboardFilter: DashboardFilter;
}

const defaultFilterSet: FilterSet = {
  complete: true,
};

export default function TableView({
  isUser,
  dataDescription,
  dashboardFilter,
}: TableViewProps) {
  const dispatch = useDispatch();
  const skillProgressUserList = useSelector(
    (state: RootState) => state.skillProgressUserList
  );
  const skillProgressStoreList = useSelector(
    (state: RootState) => state.skillProgressStoreList
  );
  const sessionProgressUserList = useSelector(
    (state: RootState) => state.sessionProgressUserList
  );
  const sessionProgressStoreList = useSelector(
    (state: RootState) => state.sessionProgressStoreList
  );
  const transcriptionKeyword = useSelector(
    (state: RootState) => state.transcriptionKeyword
  );

  const [csvExportModalVisible, setCsvExportModalVisible] = useState<
    "activity" | "session" | null
  >(null);
  const [skillProgressMainExpandToggle, setSkillProgressMainExpandToggle] =
    useState(false);
  const [skillProgressUserListLoading, setSkillProgressUserListLoading] =
    useState(false);
  const [sessionProgressUserListLoading, setSessionProgressUserListLoading] =
    useState(false);
  const [tableFilters, setTableFilters] = useState<FilterSet>(defaultFilterSet);
  const [activity, setActivity] = useState<SessionsCollection>();
  const showIncompleteSessions = !tableFilters.complete;
  const [selectedTableTab, setSelectedTableTab] =
    useState<DashboardTableTabType>("activity");

  const [
    getStaffSessionList,
    {
      data: staffSessionListData,
      loading: staffSessionListLoading,
      fetchMore: fetchMoreSessions,
      networkStatus: fetchSessionListStatus,
    },
  ] = useLazyQuery<GetSessionListQuery>(GET_STAFF_SESSION_LIST, {
    variables: {
      getStaffSessionListRequest: {
        startDateStr: formattedDateString(dashboardFilter.dateRangeStart),
        endDateStr: formattedDateString(dashboardFilter.dateRangeEnd),
        districtId: dashboardFilter.districtId,
        userId: dashboardFilter.userId,
        regionId: dashboardFilter.regionId,
        skillId: dashboardFilter.skillId,
        storeId: dashboardFilter.storeId,
        isComplete: tableFilters.complete,
        isWithVideo: tableFilters.withVideo,
        isWithTranscript: tableFilters.withTranscript,
        transcriptSearch: transcriptionKeyword,
      },
    },
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      UpduoAnalytics.logEvent("user-dashboard-activity-getstaffsession-error", {
        error: error.message,
      });
      console.error("ERROR getStaffSessionList", error);
    },
  });

  useEffect(() => {
    // when the result of getStaffSessionList changes, change the activity state.
    // We do this here instead of in `onComplete` so that the request can be cached
    // (onComplete only calls after a load from the server). The result needs to be
    // saved in a useState instead of using it directly so that we can modify it
    // when the user marks a session complete/incomplete
    const data = staffSessionListData;
    if (data) {
      setActivity({
        totalCount: data?.staffSessions?.totalCount ?? 0,
        completedCount: data?.staffSessions?.completedCount ?? 0,
        sessions: getNodes(data.staffSessions),
      });
    }
  }, [staffSessionListData]);

  useEffect(() => {
    // load session list the first time the user selects the activity tab
    if (
      selectedTableTab === "activity" &&
      !activity &&
      !staffSessionListLoading
    ) {
      getStaffSessionList();
    }
  }, [selectedTableTab, activity, getStaffSessionList]);

  useOnScrollToBottom(() => {
    if (
      selectedTableTab === "activity" &&
      !staffSessionListLoading &&
      staffSessionListData?.staffSessions?.pageInfo?.hasNextPage
    ) {
      fetchMoreSessions({
        variables: {
          cursor: staffSessionListData.staffSessions.pageInfo.endCursor,
        },
      });
    }
  });

  const updateSession = (
    sessionId: string,
    changes: Partial<
      Pick<SkaleSessionType, "isCancelled" | "allowSharing" | "masteryEdited">
    >
  ) => {
    setActivity((old) =>
      old
        ? {
            ...old,
            sessions: old.sessions.map((x) =>
              x.id === sessionId ? { ...x, ...changes } : x
            ),
          }
        : old
    );
  };

  useEffect(() => {
    setSkillProgressUserListLoading(false);
  }, [skillProgressUserList]);

  useEffect(() => {
    setSessionProgressUserListLoading(false);
  }, [sessionProgressUserList]);

  useEffect(() => {
    let isAllExpanded: boolean;
    switch (selectedTableTab) {
      case "sessionProgress":
        isAllExpanded = _handleCheckIfSessionProgressAllExpanded();
        if (!isAllExpanded) {
          return;
        }
        _handleToggleSessionProgressUserList("all");
        break;
      case "skillProgress":
        isAllExpanded = _handleCheckIfSkillProgressAllExpanded();
        if (!skillProgressMainExpandToggle && !isAllExpanded) {
          return;
        }
        _handleToggleSkillProgressUserList("all");
        break;
      // case "curriculumStat":
      //   _handleToggleCurriculumStatSkillList("all");
      //   break;
      default:
        break;
    }
  }, [
    skillProgressMainExpandToggle,
    // curriculumStatMainExpandToggle
  ]);

  const _handleCheckIfSessionProgressAllExpanded = useCallback(() => {
    const populatedStoreIds = new Set();
    sessionProgressUserList.forEach((user) => {
      if (user.store !== null) {
        populatedStoreIds.add(user.store);
      }
    });
    return sessionProgressStoreList.length === populatedStoreIds.size;
  }, [sessionProgressUserList, sessionProgressStoreList]);

  const _handleCheckIfSkillProgressAllExpanded = useCallback(() => {
    const populatedStoreIds = new Set();
    skillProgressUserList.forEach((user) => {
      if (user.store !== null) {
        populatedStoreIds.add(user.store);
      }
    });
    return skillProgressStoreList.length === populatedStoreIds.size;
  }, [skillProgressUserList, skillProgressStoreList]);

  const _handleToggleSkillProgressUserList = (storeId) => {
    // Prevent duplicate rapid clicks
    const isMainToggle = storeId === "all";
    const skillProgressUserListStoreMembers = skillProgressUserList.filter(
      (user) => user.store === storeId
    );
    const isAllExpanded = _handleCheckIfSkillProgressAllExpanded();
    if (isMainToggle && isAllExpanded) {
      if (!skillProgressMainExpandToggle) {
        // Collapse all stores row details when toggle off to collapse all rows
        dispatch(ActionCreators.setSkillProgressUserList([]));
      }
      return;
    } else if (!isMainToggle && skillProgressUserListStoreMembers.length > 0) {
      // Collapse store row details when clicking on store row
      const newSkillProgressUserList = skillProgressUserList.filter(
        (user) => user.store !== storeId
      );
      dispatch(
        ActionCreators.setSkillProgressUserList(newSkillProgressUserList)
      );
    } else {
      // Prevent duplicate API calls arising from rapid clicks
      if (skillProgressUserListLoading) {
        return;
      }
      setSkillProgressUserListLoading(true);
      // Expand stores row details when toggle on or store row click
      // Used to get user list, which includes aggregated mastery and other stats
      callApi(
        "getSkillProgressUserList",
        undefined,
        (responseJson) => {
          const oldUserList = isMainToggle
            ? []
            : skillProgressUserList.filter((user) => user.store !== storeId);
          let newSkillProgressUserList: any[] =
            oldUserList.concat(responseJson);
          if (newSkillProgressUserList.length > 1) {
            newSkillProgressUserList = newSkillProgressUserList.sort((a, b) => {
              let result = b.score - a.score;
              if (result === 0) {
                if (a.first_name > b.first_name) {
                  result = 1;
                } else if (a.first_name < b.first_name) {
                  result = -1;
                } else {
                  result = 0;
                }
              }
              return result;
            });
          }
          dispatch(
            ActionCreators.setSkillProgressUserList(newSkillProgressUserList)
          );
        },
        undefined,
        undefined,
        getQueryString(isMainToggle ? null : { store_id: storeId })
      );
    }
  };

  const _handleToggleSessionProgressUserList = (storeId) => {
    // Prevent duplicate rapid clicks
    const isMainToggle = storeId === "all";
    const sessionProgressUserListStoreMembers = sessionProgressUserList.filter(
      (user) => user.store === storeId
    );
    const isAllExpanded = _handleCheckIfSessionProgressAllExpanded();
    if (isMainToggle && isAllExpanded) {
      // Collapse all stores row details when toggle off to collapse all rows
      dispatch(ActionCreators.setSessionProgressUserList([]));
      return;
    } else if (
      !isMainToggle &&
      sessionProgressUserListStoreMembers.length > 0
    ) {
      // Collapse store row details when clicking on store row
      const newSessionProgressUserList = sessionProgressUserList.filter(
        (user) => user.store !== storeId
      );
      dispatch(
        ActionCreators.setSessionProgressUserList(newSessionProgressUserList)
      );
    } else {
      if (sessionProgressUserListLoading) {
        return;
      }
      setSessionProgressUserListLoading(true);
      // Expand stores row details when toggle on or store row click
      // Used to get user list, which includes aggregated mastery and other stats
      callApi(
        "getSessionProgressUserList",
        undefined,
        (responseJson) => {
          const oldSessionProgressUserList = isMainToggle
            ? []
            : sessionProgressUserList.filter((user) => user.store !== storeId);
          let newSessionProgressUserList: any[] =
            oldSessionProgressUserList.concat(responseJson);
          newSessionProgressUserList = newSessionProgressUserList.sort(
            (a, b) => {
              if (a.first_name > b.first_name) {
                return 1;
              } else if (a.first_name < b.first_name) {
                return -1;
              } else {
                return 0;
              }
            }
          );
          dispatch(
            ActionCreators.setSessionProgressUserList(
              newSessionProgressUserList
            )
          );
          setSessionProgressUserListLoading(false);
        },
        undefined,
        undefined,
        getQueryString(isMainToggle ? null : { store_id: storeId })
      );
    }
  };

  let tableName: string;
  //Shown table title
  switch (selectedTableTab) {
    case "activity":
      tableName = "Activity";
      break;
    case "sessionProgress":
      tableName = "Session Progress";
      break;
    default:
      tableName = `Breakdown - Total ${dataDescription}`;
      break;
  }
  const activityLength = getSessions(activity, showIncompleteSessions).length;

  return (
    <View>
      <View
        style={{
          borderColor: upduoLightestGray,
          borderWidth: 1,
          borderRadius: 12,
        }}
      >
        <TableHeader
          tableName={tableName}
          isUser={isUser}
          handleExportCsv={() => {
            // TODO clean this up
            switch (selectedTableTab) {
              case "sessionProgress":
                setCsvExportModalVisible("session");
                break;
              case "activity":
                setCsvExportModalVisible("activity");
                break;
            }
          }}
          csvLoading={false}
          selectedTab={selectedTableTab}
          onTabChoice={(tabChoice) => {
            setSelectedTableTab(tabChoice);
          }}
          filters={tableFilters}
          setFilters={setTableFilters}
        />
        {(() => {
          switch (selectedTableTab) {
            case "sessionProgress":
              return isUser ? <Breakdown /> : null;
            case "breakdown":
              return <Breakdown />;
            case "skillProgress":
              return (
                <SkillProgress
                  mainExpandToggle={skillProgressMainExpandToggle}
                  onSetMainExpandToggle={(newValue) => {
                    setSkillProgressMainExpandToggle(newValue);
                  }}
                  onExpandStore={(storeId) => {
                    _handleToggleSkillProgressUserList(storeId);
                  }}
                />
              );
            case "activity":
              return (
                <Activity
                  showIncompleteSessions={showIncompleteSessions}
                  sessionsLoading={
                    staffSessionListLoading &&
                    fetchSessionListStatus !== NetworkStatus.fetchMore
                  }
                  activityObject={activity}
                  onUpdateSession={updateSession}
                />
              );
          }
        })()}
      </View>
      <View
        style={{
          flexDirection: "row",
          height: 50,
          alignItems: "center",
        }}
      >
        {selectedTableTab === "activity" && activity ? (
          <View
            style={{
              alignSelf: "flex-start",
              flexGrow: 1,
              flexBasis: 1,
            }}
          >
            <Text
              style={{
                paddingTop: 10,
                paddingLeft: 15,
                fontWeight: "bold",
                color: skaleWebTextGray,
              }}
            >
              {`Showing ${activityLength} of ${
                showIncompleteSessions
                  ? activity?.totalCount || 0
                  : activity?.completedCount || 0
              } Sessions`}
            </Text>
          </View>
        ) : null}
        <PropagateLoader
          color={skaleMainBlue}
          loading={
            selectedTableTab === "activity" &&
            fetchSessionListStatus === NetworkStatus.fetchMore
          }
        />
        <View style={{ flexGrow: 1, flexBasis: 1 }} />
      </View>
      <CsvModal
        startDate={dashboardFilter.dateRangeStart || undefined}
        endDate={dashboardFilter.dateRangeEnd || undefined}
        visible={csvExportModalVisible !== null}
        exportType={csvExportModalVisible || ""}
        onClose={() => setCsvExportModalVisible(null)}
      />
    </View>
  );
}
